import { AlertTriangle, Lock, ShieldAlert, WifiOff } from "lucide-react";

import { getApiErrorCode, getApiErrorMessage } from "@/shared/lib/http-errors";
import { cn } from "@/shared/lib/utils";
import { Code as ErrorCode } from "@/store/apis/adminApi.generated";

interface AuthErrorAlertProps {
  /** Error crudo del `.unwrap().catch(err)` o `useForm` root. */
  error: unknown;
  /** Fallback message si no logramos derivar nada del error. */
  fallback?: string;
  className?: string;
}

type Tone = "destructive" | "warning" | "neutral";

interface Resolved {
  tone: Tone;
  Icon: typeof AlertTriangle;
  title: string;
  message: string;
}

/**
 * Alert de auth tipo-aware. Mira el `code` del `ErrorResponse` del back
 * y adapta tono + copy. Si el error no viene del back (fetch error,
 * timeout), cae a neutral con mensaje de reintento.
 *
 * Un alert mal tipado trata un rate-limit igual que una credencial mal
 * tipeada — el usuario pierde la señal. Esta separación es la diferencia
 * entre "parece bugueado" y "me cuida".
 */
export function AuthErrorAlert({ error, fallback, className }: AuthErrorAlertProps) {
  if (!error) return null;
  const resolved = resolve(error, fallback);

  const tones: Record<Tone, string> = {
    destructive:
      "border-destructive/25 bg-destructive/[0.06] text-destructive [--icon:theme(colors.destructive.DEFAULT)]",
    warning:
      "border-amber-500/30 bg-amber-500/[0.08] text-amber-900 [--icon:theme(colors.amber.600)] dark:text-amber-200",
    neutral: "border-border bg-muted/60 text-foreground [--icon:theme(colors.muted.foreground)]",
  };

  return (
    <div
      role="alert"
      aria-live="assertive"
      className={cn(
        "flex animate-fade-in-up items-start gap-3 rounded-xl border p-3.5 text-sm",
        tones[resolved.tone],
        className,
      )}
    >
      <resolved.Icon className="mt-0.5 h-4 w-4 shrink-0" style={{ color: "var(--icon)" }} />
      <div className="min-w-0 flex-1">
        <p className="font-medium leading-tight">{resolved.title}</p>
        <p className="mt-1 text-[13px] leading-relaxed opacity-85">{resolved.message}</p>
      </div>
    </div>
  );
}

function resolve(error: unknown, fallback?: string): Resolved {
  const code = getApiErrorCode(error);
  const message = getApiErrorMessage(error, fallback ?? "No pudimos completar la acción.");

  // Network / fetch error → sin `status` numérico útil.
  if (!code && isLikelyNetworkError(error)) {
    return {
      tone: "neutral",
      Icon: WifiOff,
      title: "Sin conexión",
      message: "Revisa tu internet e intenta de nuevo.",
    };
  }

  switch (code) {
    case ErrorCode.ErrRateLimited:
      return {
        tone: "warning",
        Icon: AlertTriangle,
        title: "Demasiados intentos",
        message:
          "Espera unos segundos antes de volver a intentar. Si sigue fallando, contacta a un super admin.",
      };
    case ErrorCode.ErrInvalidCredentials:
      return {
        tone: "destructive",
        Icon: Lock,
        title: "Credenciales inválidas",
        message: "Revisa tu correo y contraseña e intenta de nuevo.",
      };
    case ErrorCode.ErrUnauthorized:
    case ErrorCode.ErrInvalidSession:
      return {
        tone: "destructive",
        Icon: ShieldAlert,
        title: "Sesión no válida",
        message: "Tu sesión no se pudo verificar. Vuelve a iniciar sesión.",
      };
    case ErrorCode.ErrForbidden:
      return {
        tone: "destructive",
        Icon: ShieldAlert,
        title: "Acceso denegado",
        message: "Tu cuenta no tiene permisos para esta acción.",
      };
    case ErrorCode.ErrMfaRequired:
      return {
        tone: "neutral",
        Icon: ShieldAlert,
        title: "Verificación requerida",
        message: "Confirma el código de tu app autenticadora para continuar.",
      };
    case ErrorCode.ErrValidation:
      return {
        tone: "destructive",
        Icon: AlertTriangle,
        title: "Datos inválidos",
        message,
      };
    case ErrorCode.ErrInternal:
      return {
        tone: "destructive",
        Icon: AlertTriangle,
        title: "Error del servidor",
        message: "Algo falló del lado del servidor. Si persiste, avisa al equipo técnico.",
      };
    default:
      return {
        tone: "destructive",
        Icon: AlertTriangle,
        title: "Ocurrió un error",
        message,
      };
  }
}

function isLikelyNetworkError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  if ("status" in error) {
    const status = (error as { status?: unknown }).status;
    return status === "FETCH_ERROR" || status === "TIMEOUT_ERROR";
  }
  return false;
}
